import { FieldGroup, ImageUploader } from '@/ui'
import { ErrorMessage } from '@hookform/error-message'
import { Controller, useForm } from 'react-hook-form'
import { GameStore, GameStoreName } from '@/api/dashboard'
import { urlRegexp } from '@/libs'
import { useEffect, useState } from 'react'
import { ImageSize } from '@/components/ui/ImageUploader/ValueContainer'
import { S3Bucket } from '@/types'
import {
  Button,
  FormErrorMessage,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalProps,
  ModalTitle,
  Select,
} from '@dashboard/ui'
import { useTranslation } from 'react-i18next'
import { STORES_ICONS_MAP } from '../../../icons/Icons'

interface ConfirmDialogProps extends ModalProps {
  onAdd: (item: Item) => void
  currentLinks: GameStore[]
}

interface Item {
  name: GameStoreName
  url: string
  /** Custom Name */
  custom_name?: string
  /** Custom Icon Url */
  custom_icon_url?: string

  custom_hub_button_icon_url?: string
}

export const GameStoreModal = (props: ConfirmDialogProps) => {
  const { ...rest } = props
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<Item>({})

  const [url, name] = watch(['url', 'name'])

  const [customSocial, setCustomSocial] = useState<GameStoreName | null>(null)

  const icons: Record<string, string> = STORES_ICONS_MAP

  const getSocialStoreName = (url: string) => {
    if (!url || !url.startsWith('https://')) {
      return null
    }

    if (url.includes('apple.com')) {
      return GameStoreName.AppStore
    } else if (url.includes('play.google.com/pc-store')) {
      return GameStoreName.GooglePlayPc
    } else if (url.includes('play.google.com')) {
      return GameStoreName.GooglePlay
    } else if (url.includes('steampowered.com')) {
      return GameStoreName.Steam
    } else if (url.includes('amazon.')) {
      return GameStoreName.Amazon
    } else if (url.includes('appg.al') || url.includes('appgallery.huawei.com')) {
      return GameStoreName.Huawei
    } else if (url.includes('onestore.net') || url.includes('onestore.co')) {
      return GameStoreName.OneStore
    } else if (url.includes('apps.microsoft.com')) {
      return GameStoreName.MicrosoftStore
    } else if (url.includes('galaxystore.samsung.com')) {
      return GameStoreName.GalaxyStore
    } else if (url.includes('itunes.apple.com')) {
      return GameStoreName.MacAppStore
    } else {
      return GameStoreName.Custom
    }
  }

  const onSubmit = async (data: Item) => {
    data.url = data.url.replace(/\s+/g, '')
    data.name = (getSocialStoreName(data.url) as GameStoreName) || GameStoreName.Custom

    if (data.name != GameStoreName.Custom) {
      data.custom_name = undefined
      data.custom_icon_url = undefined
      data.custom_hub_button_icon_url = undefined
    }

    if (data.name == GameStoreName.Custom && customSocial) {
      data.name = customSocial
    }

    rest.onAdd(data)
    rest.onClose?.()
  }

  const onCancel = async () => {
    rest.onClose?.()
  }

  useEffect(() => {
    const value = getSocialStoreName(url) || GameStoreName.Custom
    setValue('name', value)
    if (value == GameStoreName.Custom) {
      setCustomSocial(GameStoreName.Custom)
    }
  }, [url])

  const customStoresOptions = [
    {
      name: GameStoreName.Custom,
      label: t('game-settings.custom'),
    },
  ]

  if (!props.currentLinks.some(link => link.name === GameStoreName.DownloadApk)) {
    customStoresOptions.push({
      name: GameStoreName.DownloadApk,
      label: t('game-settings.download-apk'),
    })
  }

  if (!props.currentLinks.some(link => link.name === GameStoreName.PlayNow)) {
    customStoresOptions.push({
      name: GameStoreName.PlayNow,
      label: t('game-settings.play-now'),
    })
  }

  return (
    <Modal {...rest} onClose={() => props.onClose?.()}>
      <ModalContent>
        <ModalTitle>{t('game-settings.store-store-add')}</ModalTitle>

        <FieldGroup label={t('game-settings.add.enter-url')}>
          <Input
            autoFocus
            {...register('url', {
              required: t('validation.required'),
              pattern: { value: urlRegexp, message: t('validation.invalid_url') },
            })}
            maxLength={500}
          />
          <ErrorMessage
            name="url"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
        {getSocialStoreName(url) == GameStoreName.Custom && (
          <>
            <FieldGroup label={t('game-settings.type-link')}>
              <Select
                value={customSocial}
                onChange={value => {
                  setCustomSocial(value)
                }}
                items={[
                  ...customStoresOptions.map(it => ({
                    children: (
                      <div className="flex items-center gap-2">
                        <img src={icons[it.name] || ''} alt={it.name} className="size-6" />
                        <span>{it.label}</span>
                      </div>
                    ),
                    value: it.name,
                  })),
                ]}
              />
            </FieldGroup>
            <FieldGroup label={t('game-settings.custom-name')}>
              <Input
                {...register('custom_name', {
                  required: name == GameStoreName.Custom ? t('validation.required') : false,
                })}
                maxLength={50}
              />
              <ErrorMessage
                name="custom_name"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FieldGroup>
            <FieldGroup label={t('game-settings.add.custom-url')}>
              <Controller
                name="custom_icon_url"
                control={control}
                rules={{
                  pattern: { value: urlRegexp, message: t('validation.invalid_url') },
                  required: name == GameStoreName.Custom ? t('validation.required') : false,
                }}
                render={({ field }) => (
                  <ImageUploader
                    disabled={name != GameStoreName.Custom}
                    imageSize={ImageSize.contain}
                    {...field}
                    accept={{
                      'image/jpeg': ['.jpeg', '.jpg'],
                      'image/png': [],
                      'image/webp': [],
                      'image/gif': [],
                      'image/svg+xml': [],
                    }}
                    bucket={S3Bucket.hub}
                  />
                )}
              />
              <ErrorMessage
                name="custom_icon_url"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FieldGroup>

            <FieldGroup label={t('game-settings.custom_hub_button_icon_url')}>
              <Controller
                name="custom_hub_button_icon_url"
                control={control}
                rules={{
                  pattern: { value: urlRegexp, message: t('validation.invalid_url') },
                  required: name == GameStoreName.Custom ? t('validation.required') : false,
                }}
                render={({ field }) => (
                  <ImageUploader
                    disabled={name != GameStoreName.Custom}
                    imageSize={ImageSize.contain}
                    {...field}
                    accept={{
                      'image/jpeg': ['.jpeg', '.jpg'],
                      'image/png': [],
                      'image/webp': [],
                      'image/gif': [],
                      'image/svg+xml': [],
                    }}
                    bucket={S3Bucket.hub}
                  />
                )}
              />
              <ErrorMessage
                name="custom_hub_button_icon_url"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FieldGroup>
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="outline" color="secondary" type="button" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>{t('Add')}</Button>
      </ModalFooter>
    </Modal>
  )
}
